import { useTranslation } from 'react-i18next'

import { Box, Checkbox, Flex, Link, Text } from '@enechain/ecloud-designsystem'
import { FaArrowUpRightFromSquareRegular } from '@enechain/ecloud-icons'

import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import {
  FilterSection,
  FilterToolbar as FilterToolbarContainer,
} from '~/components/common/filters'
import { iconSize } from '~/constants/iconSize'
import { civilDateToInputValue, parseDateInputValue } from '~/models/civilDate'
import type { AreaDef } from '~/pages/SpotPrice/constants'
import { AREAS } from '~/pages/SpotPrice/constants'

/** `CompactCivilDateField` の id (label 経由の accessible name / test の入口)。 */
const DATE_FROM_FIELD_ID = 'spot-price-delivery-date-from'
const DATE_TO_FIELD_ID = 'spot-price-delivery-date-to'

type Props = {
  dateFrom: string
  dateTo: string
  maxDate: string
  visibleAreas: Set<string>
  onDateFromChange: (v: string) => void
  onDateToChange: (v: string) => void
  onAreaToggle: (code: string, checked: boolean) => void
  onMasterToggle: (checked: boolean) => void
}

export const FilterToolbar = ({
  dateFrom,
  dateTo,
  maxDate,
  visibleAreas,
  onDateFromChange,
  onDateToChange,
  onAreaToggle,
  onMasterToggle,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // `maxDate` は文字列 (YYYY-MM-DD)。 popup 側は CivilDate を受けるので変換する。
  // 未来日を選ばせない上限は index.tsx が同じ値で clamp 済みなので、 ここは
  // **popup 上でも選べなくする**ための可視化 (issue #6361 で `max` が入った)。
  const maxCivilDate = parseDateInputValue(maxDate)

  // 全エリア master = 9 エリア (system 除く) が全 ON かどうかで indeterminate/checked を決める
  const nonSystemAreas = AREAS.filter((a) => !a.isSystem)
  const checkedCount = nonSystemAreas.filter((a) =>
    visibleAreas.has(a.code),
  ).length
  const allChecked = checkedCount === nonSystemAreas.length
  const someChecked = checkedCount > 0 && !allChecked

  return (
    // gap/padding/borderRadius/backgroundColor/外側 margin は共通 FilterToolbar の
    // 内部固定値 (frontend-design-unification 設計書 §5.1) を使う。gap は design
    // `.ecl-toolbar` 正本値 "16px 20px" に是正済み (旧 "8px 20px" は誤値)。
    // 従来ここで渡していた gap="24px" / borderRadius="md" / backgroundColor="Surface.secondary"
    // は design 実測と不一致 (= 灰色カード化・角丸縮小・余白拡大) だったため撤去した
    <FilterToolbarContainer>
      {/* 受渡日。 DS `Calendar` primitive の上に立つ共通 date field
          (`CompactCivilDateField`) を使う。 8080 と同じ **2 欄** の形は保つ。

          [解消 2026-08-31] 以前はここに native `<input type="date">` を DS `Input` で
          包み、 picker indicator を CSS で隠して入力欄 click で `showPicker()` を
          呼ぶ自前実装を置いていた。 8080 の見た目に寄せるための暫定で、
          「`CompactCivilDateField` へ寄せるのは follow-up」 と据え置いていたもの
          (技術的な gap = popup の maxDate 配線は issue #6361 で解消済みだった)。
          デザイナー指示 (2026-08-31「受渡日 > DS の date picker」) で解消した。

          DS `DatePicker` ではなく `CompactCivilDateField` を使うのは、 前者が
          padding=6 + 選択日 label 帯を baked-in で持ち design と合わないため
          (同 component の docstring に記録済みの既存判断)。 広域マスタ・類似日・
          同時同量など他画面もこれを使っており、 日付入力の見た目が 1 本に揃う。

          state は文字列 (YYYY-MM-DD) のまま。 `CivilDate` との相互変換は
          `models/civilDate` の helper が吸収する (= DateRangePicker 移行で必要な
          state 形の変更は伴わない)。 */}
      <FilterSection label={t('page:spotPrice.filter.deliveryDate')}>
        {/* 幅は共通の日付欄の幅 (曜日付きの値が収まる幅)。 [design 差異] design の
            `.date-input-wrap > input.filter-input.ecl-date-input` は 138px で、
            曜日を付けた (2026-10-20 デザイナー指示) 値が収まらない。 */}
        <Flex alignItems="center" gap={1}>
          <Box width={COMPACT_DATE_FIELD_WIDTH}>
            <CompactCivilDateField
              aria-label={t('page:spotPrice.filter.deliveryDateFrom')}
              id={DATE_FROM_FIELD_ID}
              max={maxCivilDate}
              onChange={(d) => {
                // 空欄化 (キーボードで消した時) は既存挙動と揃えて無視する
                // — 受渡日は必須で、 未入力にすると一覧の問い合わせが組めない。
                if (d !== undefined) onDateFromChange(civilDateToInputValue(d))
              }}
              value={parseDateInputValue(dateFrom)}
            />
          </Box>
          {/* 8080 は文字サイズを継承 (14px) */}
          <Text color="Text.midEmphasis">～</Text>
          <Box width={COMPACT_DATE_FIELD_WIDTH}>
            <CompactCivilDateField
              aria-label={t('page:spotPrice.filter.deliveryDateTo')}
              id={DATE_TO_FIELD_ID}
              max={maxCivilDate}
              onChange={(d) => {
                if (d !== undefined) onDateToChange(civilDateToInputValue(d))
              }}
              value={parseDateInputValue(dateTo)}
            />
          </Box>
        </Flex>
      </FilterSection>

      {/* データソース (外部リンク) */}
      <FilterSection label={t('page:spotPrice.filter.dataSource')}>
        {/* design bidding/spot-price.html の出典リンクは `.ecl-text-link` +
            external-link icon。色・下線・inline-flex/gap は DS Link 既定が design 値と
            一致するので指定しない (旧 `Text.link` は design の accent primary と別色)。 */}
        <Link
          href="https://www.jepx.jp/electricpower/market-data/spot/"
          rel="noopener noreferrer"
          target="_blank"
        >
          {/* 出典リンク文言は API 由来データではなく静的な UI ラベルなので i18n
              経由にする (旧「i18n 対象外」コメントは誤り)。ja/en 両方を定義済み。
              SpotPrice ページの他のフィルタ文言 (dataSource 等) の en 化は
              未着手で、#6831 の残タスクとして ja へフォールバックする。 */}
          {t('page:spotPrice.filter.dataSourceLink')}
          {/* 外部リンク icon は 12px (design は素の SVG 属性 width=12、
              `iconSize.externalLink`)。
              **旧実装は `chakra.svg` に `width="12"` を渡していて 48px で出ていた** —
              chakra の style prop は数値文字列を spacing token として解決するので
              `12` は 3rem になる (2026-08-31 デザイナー指摘「external アイコンが
              大きすぎる」)。 */}
          <FaArrowUpRightFromSquareRegular boxSize={iconSize.externalLink} />
        </Link>
      </FilterSection>

      {/* エリアフィルタ */}
      <FilterSection grow label={t('page:spotPrice.filter.area')}>
        {/* 全エリア master checkbox (system 除く) */}
        <Checkbox
          checked={someChecked ? 'indeterminate' : allChecked}
          onCheckedChange={(detail: { checked: boolean | 'indeterminate' }) => {
            onMasterToggle(detail.checked === true)
          }}
        >
          {t('page:spotPrice.filter.allAreas')}
        </Checkbox>

        {/* システムプライス (divider で分離・master 対象外) */}
        {AREAS.filter((a) => a.isSystem).map((a: AreaDef) => (
          <AreaCheckbox
            key={a.code}
            area={a}
            checked={visibleAreas.has(a.code)}
            onToggle={onAreaToggle}
          />
        ))}

        {/* divider (8080 `.ecl-toolbar-divider` = 1px × 20px) */}
        <Box
          width="1px"
          height="20px"
          aria-hidden="true"
          backgroundColor="Border.lowEmphasis"
        />

        {/* 9 エリア */}
        {nonSystemAreas.map((a: AreaDef) => (
          <AreaCheckbox
            key={a.code}
            area={a}
            checked={visibleAreas.has(a.code)}
            onToggle={onAreaToggle}
          />
        ))}
      </FilterSection>
    </FilterToolbarContainer>
  )
}

type AreaCheckboxProps = {
  area: AreaDef
  checked: boolean
  onToggle: (code: string, checked: boolean) => void
}

const AreaCheckbox = ({
  area,
  checked,
  onToggle,
}: AreaCheckboxProps): React.ReactElement => (
  <Checkbox
    checked={checked}
    /**
     * checkbox 本体をそのエリアの系列色にする (= 凡例ドットの代わり)。 design は
     * 未チェックでも枠だけ色を付け、 チェックで塗りつぶす:
     * `.sm-area-chk-wrap input { border-color: var(--area-c) }` /
     * `:checked { background-color: var(--area-c); border-color: var(--area-c) }`
     * (`design/bidding/spot-price.html:25-31`)。
     *
     * **CSS 変数 (`--checkbox-color` 等) を Checkbox に渡す方式は効かない。**
     * DS `Checkbox` (chakra v3) は可視の箱を `[data-part="control"]` という**別要素**で
     * 描き、 native input は sr-only なので、 input 側に置いた変数は兄弟の control へ
     * 継承されない (`ForecastChartLegend` の JSDoc に 2026-08-12 の実 DOM 確認記録あり)。
     * 2026-08-31 デザイナー指摘「エリアの checkbox 背景色をグラフの系列色と同じに」は
     * 旧実装がこの方式で**全て DS 既定の青のまま**だったため。
     *
     * 同じ画面群で先に解決している `ForecastChartLegend` と同じく `css` prop で
     * control を直接指定する。 DS recipe は `@layer recipes` 内なので、 無 layer で
     * 出力される `css` prop が hover/active 派生ごと勝つ。 disabled は
     * `:not([data-disabled])` で除外し DS 既定のフォールバックに委ねる。
     */
    css={{
      '& [data-part="control"]:not([data-disabled])': {
        borderColor: area.color,
      },
      '& [data-part="control"][data-state="checked"]:not([data-disabled])': {
        background: area.color,
        borderColor: area.color,
      },
    }}
    onCheckedChange={(detail: { checked: boolean | 'indeterminate' }) => {
      onToggle(area.code, detail.checked === true)
    }}
  >
    {/* fontSize は指定しない: DS checkbox の label 補正 (`~/constants/system.ts`
        dsFallbackFixRecipes.checkbox) が design SoT の 14px を当てる。 */}
    <Text>{area.name}</Text>
  </Checkbox>
)
